import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    CircularProgress,
    Alert,
    IconButton,
    Grid,
    Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsIcon from "@mui/icons-material/Sms";
import { StateType } from "../../../backend/store";
import { billingAPI } from "../../../backend/api/api";
import { useTranslation } from "../../../hooks/useTranslation";

interface ContactUsModalProps {
    open: boolean;
    onClose: () => void;
}

const ContactUsModal: React.FC<ContactUsModalProps> = ({
    open,
    onClose,
}) => {
    const { p, t } = useTranslation("contact_us");
    const currentUser = useSelector((state: StateType) => state.authStore.currentUser);

    const defaultName = currentUser
        ? `${currentUser.first_name} ${currentUser.last_name}`.trim()
        : "";
    const defaultEmail = currentUser?.email ?? "";

    const [name, setName] = useState(defaultName);
    const [email, setEmail] = useState(defaultEmail);
    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [emailError, setEmailError] = useState<string | null>(null);
    const [nameError, setNameError] = useState<string | null>(null);

    const handleClose = () => {
        // Reset form state on close
        setName(defaultName);
        setEmail(defaultEmail);
        setPhone("");
        setMessage("");
        setLoading(false);
        setSubmitted(false);
        setError(null);
        setEmailError(null);
        setNameError(null);
        onClose();
    };

    const validate = (): boolean => {
        let valid = true;
        
        if (!name.trim()) {
            setNameError(p("name_required"));
            valid = false;
        } else {
            setNameError(null);
        }

        if (!email.trim()) {
            setEmailError(p("email_required"));
            valid = false;
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            setEmailError(p("invalid_email"));
            valid = false;
        } else {
            setEmailError(null);
        }
        
        return valid;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setLoading(true);
        setError(null);
        try {
            await billingAPI.contactSales({
                contact_info: {
                    name: name.trim(),
                    email: email.trim(),
                    phone: phone.trim() || undefined,
                },
                message: message.trim() || undefined,
                attempted_plan: "contact_us",
            });
            setSubmitted(true);
        } catch (err) {
            setError(p("error_submit"));
        } finally {
            setLoading(false);
        }
    };

    const contactPhone = "575-741-0444";
    const cleanPhone = contactPhone.replace(/-/g, "");

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={600}>
                    {p("title")}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{ color: (theme) => theme.palette.grey[500] }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {submitted ? (
                    <Box textAlign="center" py={4}>
                        <CheckCircleIcon sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
                        <Typography variant="h5" gutterBottom fontWeight={600}>
                            {p("message_sent")}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            {p("thank_you", { email })}
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, py: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {p("intro")}
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<WhatsAppIcon />}
                                    href={`https://wa.me/1${cleanPhone}`}
                                    target="_blank"
                                    sx={{ 
                                        color: '#25D366', 
                                        borderColor: '#25D366',
                                        '&:hover': {
                                            borderColor: '#128C7E',
                                            backgroundColor: 'rgba(37, 211, 102, 0.04)'
                                        }
                                    }}
                                >
                                    WhatsApp
                                </Button>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<SmsIcon />}
                                    href={`sms:+1${cleanPhone}`}
                                    sx={{ color: '#007AFF', borderColor: '#007AFF' }}
                                >
                                    {p("text_message")}
                                </Button>
                            </Grid>
                        </Grid>

                        <Divider>
                            <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                {p("or_send_message")}
                            </Typography>
                        </Divider>

                        {error && (
                            <Alert severity="error">{error}</Alert>
                        )}

                        <TextField
                            label={t("general.name")}
                            value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (nameError) setNameError(null);
                            }}
                            required
                            fullWidth
                            size="small"
                            error={!!nameError}
                            helperText={nameError}
                        />

                        <TextField
                            label={t("general.email")}
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                if (emailError) setEmailError(null);
                            }}
                            required
                            fullWidth
                            size="small"
                            type="email"
                            error={!!emailError}
                            helperText={emailError}
                        />

                        <TextField
                            label={p("cell_phone")}
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            fullWidth
                            size="small"
                            type="tel"
                            placeholder={p("optional")}
                        />

                        <TextField
                            label={p("comment_message")}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                            placeholder={p("help_placeholder")}
                        />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {submitted ? (
                    <Button onClick={handleClose} variant="contained" fullWidth>
                        {t("common.close")}
                    </Button>
                ) : (
                    <>
                        <Button onClick={handleClose} color="inherit" disabled={loading}>
                            {t("general.cancel")}
                        </Button>
                        <Button
                            onClick={handleSubmit}
                            variant="contained"
                            disabled={loading}
                            sx={{ minWidth: 120 }}
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
                        >
                            {loading ? p("sending") : p("send_message")}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ContactUsModal;
